import { env } from "../config/env";

type ApiRequestInit = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  timeoutMs?: number;
  signal?: AbortSignal;
};

type ErrorPayload = {
  detail?: string;
  request_id?: string;
};

export class ApiError extends Error {
  status: number;
  requestId?: string;

  constructor(message: string, status: number, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
  }
}

// Tenant and platform sessions are deliberately kept on separate token slots
// (rather than one shared slot) so a tenant and a platform-admin login can
// never cross, even within the same tab/runtime.
let activeAuthToken: string | null = null;
let activePlatformAuthToken: string | null = null;

export function setAuthToken(token: string | null) {
  activeAuthToken = token;
}

export function setPlatformAuthToken(token: string | null) {
  activePlatformAuthToken = token;
}

function buildHeaders(
  body: unknown,
  headers: Record<string, string> | undefined,
  bearerToken: string | null,
) {
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  if (bearerToken && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${bearerToken}`;
  }

  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders["Content-Type"] = "application/json";
  }

  return finalHeaders;
}

function withTimeout(
  signal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort("Request timeout"), timeoutMs);
  let onAbort: (() => void) | undefined;

  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    onAbort = () => controller.abort(signal.reason);
    signal.addEventListener("abort", onAbort, {
      once: true,
    });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      if (signal && onAbort) {
        signal.removeEventListener("abort", onAbort);
      }
    },
  };
}

async function parseResponse<T>(response: Response): Promise<T | null> {
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  if (response.status === 204) return null;

  if (isJson) {
    return (await response.json()) as T;
  }

  const text = await response.text();
  return (text || null) as T | null;
}

async function performRequest<T>(
  path: string,
  init: ApiRequestInit,
  bearerToken: string | null,
): Promise<T> {
  const {
    method = "GET",
    body,
    headers,
    timeoutMs = 15000,
    signal,
  } = init;

  const requestSignal = withTimeout(signal, timeoutMs);
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    method,
    headers: buildHeaders(body, headers, bearerToken),
    body:
      body === undefined || body instanceof FormData
        ? (body as BodyInit | undefined)
        : JSON.stringify(body),
    signal: requestSignal.signal,
  }).finally(() => {
    requestSignal.cleanup();
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let requestId: string | undefined;

    try {
      const payload = (await parseResponse<ErrorPayload>(response)) || {};
      if (payload.detail) message = payload.detail;
      requestId = payload.request_id;
    } catch {
      // ignore parse errors, use fallback message
    }

    throw new ApiError(message, response.status, requestId);
  }

  return (await parseResponse<T>(response)) as T;
}

export function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  return performRequest<T>(path, init, activeAuthToken);
}

/**
 * Fetch a binary response (e.g. a visitor photo/ID document) with the tenant
 * auth header and return an object URL. The caller MUST revoke the URL when
 * done (URL.revokeObjectURL) to avoid leaking memory. A plain <img src> can't
 * be used because the endpoint requires the Authorization header.
 */
export async function apiFetchObjectUrl(path: string): Promise<string> {
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    headers: activeAuthToken ? { Authorization: `Bearer ${activeAuthToken}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(`Request failed (${response.status})`, response.status);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function platformApiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  return performRequest<T>(path, init, activePlatformAuthToken);
}
